import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaUserCheck, FaTimes } from 'react-icons/fa';
import {
  faChartPie,
  faChartBar,
  faUsers,
  faListAlt,
  faEdit,
  faTrash,
  faPlus
} from '@fortawesome/free-solid-svg-icons'; // removed faEvent
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FaTimesCircle } from 'react-icons/fa';
import { useToast } from '../contexts/ToastContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import useRolePermissions, { hasPermission } from '../hooks/useRolePermissions';

const API_BASE = 'https://dev.sliitleo.org/Backend/events.php';

const EventsPage = () => {
  const { showToast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [formData, setFormData] = useState({
    event_name: '',
    event_date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: ''
  });

  // Format time for input field
  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    if (timeString.includes(':')) {
      return timeString.split(':').slice(0, 2).join(':');
    }
    return timeString;
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Fetch all events
  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(API_BASE, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Failed to fetch events');
      const data = await response.json();
      // accept either {success, data} or raw array
      const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setEvents(rows);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to load events. Please try again.');
      showToast('Failed to load events', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Open modal for adding new event
  const openAddModal = () => {
    setCurrentEvent(null);
    setFormData({
      event_name: '',
      event_date: '',
      start_time: '',
      end_time: '',
      location: '',
      description: ''
    });
    setIsModalOpen(true);
  };

  // Open modal for editing event
  const openEditModal = (event) => {
    setCurrentEvent(event);
    setFormData({
      event_name: event.event_name,
      event_date: event.event_date,
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      location: event.location || '',
      description: event.description || ''
    });
    setIsModalOpen(true);
  };

  // Handle form submission (add/edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = currentEvent
        ? `https://dev.sliitleo.org/Backend/events.php?id=${currentEvent.id}` // fixed stray "/"
        : 'https://dev.sliitleo.org/Backend/events.php';

      const method = currentEvent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || 'Failed to save event');
      }

      await fetchEvents();
      setIsModalOpen(false);
      showToast(`Event ${currentEvent ? 'updated' : 'created'} successfully`, 'success');
    } catch (error) {
      console.error('Error saving event:', error);
      showToast(error.message || 'Failed to save event', 'error');
    }
  };

  /// State for delete confirmation
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [eventToDelete, setEventToDelete] = useState(null);

// Open delete confirmation
const confirmDelete = (eventId) => {
  setEventToDelete(eventId);
  setShowDeleteConfirm(true);
};

// Handle event deletion (POST override to avoid CORS preflight)
const handleDelete = async (shouldDelete) => {
  if (!shouldDelete || !eventToDelete) {
    setShowDeleteConfirm(false);
    setEventToDelete(null);
    return;
  }

  const id = eventToDelete; // capture before we clear it

  try {
    // Use POST + _method=DELETE with a "simple" content-type (no preflight)
    const res = await fetch(`${API_BASE}?id=${encodeURIComponent(id)}&_method=DELETE`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
      body: '' // keep empty to remain a simple request
    });

    // Try to parse JSON if server returns it; otherwise just rely on status
    let payload = null;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try { payload = await res.json(); } catch {}
    }

    if (!res.ok || (payload && payload.success === false)) {
      throw new Error((payload && payload.error) || `Failed to delete (status ${res.status})`);
    }

    // Success: close modal and optimistically remove from UI
    setShowDeleteConfirm(false);
    setEventToDelete(null);
    setEvents((prev) => prev.filter((ev) => String(ev.id) !== String(id)));
    showToast('Event deleted successfully', 'success');
  } catch (error) {
    console.error('Error deleting event:', error);
    setShowDeleteConfirm(false);
    setEventToDelete(null);
    showToast(error.message || 'Failed to delete event', 'error');
  }
};

// Fetch events on component mount
useEffect(() => {
  fetchEvents();
}, []);

  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    SLIITID: '0',
    role: 'Member',
    image: '/user.png'
  };

  // Create/Edit/Delete previously had no gate beyond page access. Fallback
  // is this page's own route access list, so nothing anyone could already
  // do stops working until an admin explicitly narrows it.
  const eventsPerm = useRolePermissions(user.role);
  const canManageEvents = hasPermission(
    'events_manage',
    eventsPerm,
    user.role,
    ['Club Performence Evaluator', 'System Admin', 'President', 'Vice President', 'Secretary', 'Treasurer', 'Co - Asst. Secretary', 'Co - Asst. Treasurer']
  );

  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 ml-0">
        <div className="content">
          {/* Header */}
          <div className="page-head">
            <div>
              <div className="eyebrow">Attendance Management</div>
              <h1>Event Management</h1>
              <p className="page-desc">Create, edit, and manage events for attendance tracking.</p>
            </div>

            {/* Section quick-nav */}
            <div className="filter-row" style={{ flexWrap: 'wrap' }}>
              <Link to="/members" className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faUsers} /> Members
              </Link>
              <Link to="/events" className="chip active" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faChartPie} /> Events
              </Link>
              <Link to="/scan-qr-code" className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faChartBar} /> Scan QR
              </Link>
              <Link to="/attendance" className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <FontAwesomeIcon icon={faListAlt} /> Attendance
              </Link>
            </div>
          </div>

          <div className="panel">
            <div className="section-head">
              <h2>All Events</h2>
              {canManageEvents && (
              <button className="btn btn-primary btn-sm" onClick={openAddModal}>
                <FontAwesomeIcon icon={faPlus} />
                <span>Add New Event</span>
              </button>
              )}
            </div>

            <div style={{ padding: '0 var(--sp-4) var(--sp-4)' }}>
              {loading ? (
                <div className="empty-state">
                  <span className="spinner lg" aria-hidden="true" />
                  <p>Loading events...</p>
                </div>
              ) : error ? (
                <div className="empty-state">
                  <p style={{ color: 'var(--error)' }}>{error}</p>
                  <button onClick={fetchEvents} className="btn btn-ghost btn-sm">
                    Retry
                  </button>
                </div>
              ) : events.length > 0 ? (
                <div className="timeline">
                  {events.map((event) => {
                    const allDay = !event.start_time && !event.end_time;
                    const eventDate = new Date(event.event_date);
                    const hasValidDate = !isNaN(eventDate);
                    const day = hasValidDate ? eventDate.toLocaleDateString(undefined, { day: '2-digit' }) : '--';
                    const mon = hasValidDate ? eventDate.toLocaleDateString(undefined, { month: 'short' }) : '';

                    return (
                      <div className="t-item" key={event.id} style={{ alignItems: 'flex-start' }}>
                        <div className="t-date">
                          <span className="d">{day}</span>
                          <span className="m">{mon}</span>
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div className="t-title truncate" title={event.event_name}>
                                {event.event_name}
                              </div>
                              <div className="t-meta">
                                {formatDate(event.event_date)}
                                {allDay ? (
                                  <span className="pill pill-info" style={{ marginLeft: 8 }}>
                                    All-day
                                  </span>
                                ) : (
                                  <>
                                    {event.start_time && ` • ${formatTimeForInput(event.start_time)}`}
                                    {event.end_time && `–${formatTimeForInput(event.end_time)}`}
                                  </>
                                )}
                              </div>
                              {event.location && (
                                <div className="t-meta" style={{ marginTop: 4 }}>
                                  📍 {event.location}
                                </div>
                              )}
                              {event.description && (
                                <p className="t-meta truncate" style={{ marginTop: 4 }} title={event.description}>
                                  {event.description}
                                </p>
                              )}

                              <div
                                style={{
                                  marginTop: 8,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 8,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <span className="t-meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <FaUserCheck /> 0 attendees
                                </span>
                                <Link to={`/attendance?event_id=${event.id}`} style={{ fontSize: 11.5, color: 'var(--link)' }}>
                                  View Attendance
                                </Link>
                              </div>
                            </div>

                            <div className="row-actions" style={{ flexShrink: 0 }}>
                              {canManageEvents && (
                              <>
                              <button
                                className="icon-btn"
                                onClick={() => openEditModal(event)}
                                aria-label={`Edit ${event.event_name}`}
                                title="Edit Event"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                className="icon-btn"
                                onClick={() => confirmDelete(event.id)}
                                aria-label={`Delete ${event.event_name}`}
                                title="Delete Event"
                                style={{ color: 'var(--error)' }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                              </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">
                    <FaCalendarAlt />
                  </div>
                  <h3>No events yet</h3>
                  <p>Get started by creating your first event.</p>
                  <button onClick={openAddModal} className="btn btn-primary" style={{ marginTop: 6 }}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Create Event</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Add/Edit Event Modal */}
          {isModalOpen && (
            <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={currentEvent ? 'Edit Event' : 'Create New Event'}>
              <div className="modal-panel panel">
                <div className="modal-head">
                  <h3>{currentEvent ? 'Edit Event' : 'Create New Event'}</h3>
                  <button onClick={() => setIsModalOpen(false)} className="icon-btn" aria-label="Close">
                    <FaTimes />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="field">
                      <label htmlFor="event_name" className="field-label">
                        Event Name <span className="req">*</span>
                      </label>
                      <input
                        id="event_name"
                        type="text"
                        name="event_name"
                        value={formData.event_name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label htmlFor="event_date" className="field-label">
                          Date <span className="req">*</span>
                        </label>
                        <input
                          id="event_date"
                          type="date"
                          name="event_date"
                          value={formData.event_date}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="field" style={{ marginBottom: 0 }}>
                        <label htmlFor="start_time" className="field-label">Start Time</label>
                        <input
                          id="start_time"
                          type="time"
                          name="start_time"
                          value={formData.start_time}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="field" style={{ marginBottom: 0 }}>
                        <label htmlFor="end_time" className="field-label">End Time</label>
                        <input
                          id="end_time"
                          type="time"
                          name="end_time"
                          value={formData.end_time}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className="field md:col-span-2" style={{ marginBottom: 0 }}>
                        <label htmlFor="location" className="field-label">Location</label>
                        <input
                          id="location"
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          placeholder="e.g., Main Hall, Room 101"
                        />
                      </div>
                    </div>

                    <div className="field" style={{ marginBottom: 0, marginTop: 'var(--sp-4)' }}>
                      <label htmlFor="description" className="field-label">Description</label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Add event details..."
                      ></textarea>
                    </div>
                  </div>

                  <div className="modal-foot">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="btn btn-ghost">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {currentEvent ? 'Update Event' : 'Create Event'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="modal-overlay" role="alertdialog" aria-modal="true" aria-label="Delete Event">
              <div className="modal-panel panel">
                <div className="modal-body" style={{ textAlign: 'center' }}>
                  <div
                    className="empty-icon"
                    style={{ margin: '0 auto var(--sp-4)', background: 'rgba(226,100,92,.15)', color: 'var(--error)' }}
                  >
                    <FaTimesCircle />
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 700 }}>Delete Event</h3>
                  <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 8 }}>
                    Are you sure you want to delete this event? This action cannot be undone.
                  </p>
                </div>
                <div className="modal-foot" style={{ justifyContent: 'center' }}>
                  <button type="button" onClick={() => handleDelete(false)} className="btn btn-ghost">
                    Cancel
                  </button>
                  <button type="button" onClick={() => handleDelete(true)} className="btn btn-danger">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default EventsPage;
